import { AnalyticsEvent, type AnalyticsEventName } from "./analytics/events";

type AnalyticsDataValue = string | number | boolean | null | undefined | { [key: string]: AnalyticsDataValue } | AnalyticsDataValue[];
type AnalyticsData = Record<string, AnalyticsDataValue> | undefined;

declare global {
  interface Window {
    plausible?: (event: string, options?: { props?: AnalyticsData }) => void;
    // posthog is declared in posthogClient.ts to avoid type conflicts
  }
}

/**
 * Track an analytics event
 * Supports multiple analytics providers: plausible, posthog, or internal
 */
export function trackEvent(
  event: AnalyticsEventName | string,
  data?: AnalyticsData
): void {
  // Only track if analytics is enabled
  if (typeof window === "undefined") return;
  
  const provider = process.env.NEXT_PUBLIC_ANALYTICS_PROVIDER;
  if (!provider) {
    console.debug('[Analytics] No provider configured, skipping event:', event);
    return;
  }

  // Strip PII from data before sending
  const sanitizedData = sanitizeEventData(data);

  switch (provider) {
    case "plausible":
      if (window.plausible) {
        window.plausible(event, { props: sanitizedData });
      }
      break;

    case "posthog":
      // Use the initialized PostHog instance from posthogClient
      if (window.posthog && window.posthog.__loaded) {
        window.posthog.capture(event, sanitizedData as Record<string, string | number | boolean | null | undefined>);
        console.debug('[Analytics] Event sent to PostHog:', event, sanitizedData);
      } else {
        console.debug('[Analytics] PostHog not loaded, skipping event:', event, { 
          posthogExists: !!window.posthog, 
          isLoaded: window.posthog?.__loaded 
        });
      }
      break;

    case "internal":
      // Send to internal analytics endpoint
      fetch("/api/analytics/track", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          event,
          data: sanitizedData,
        }),
        // Don't block on analytics requests
        keepalive: true,
      }).catch((err) => {
        // Silently fail - analytics shouldn't break the app
        console.debug("Analytics tracking failed:", err);
      });
      break;

    default:
      console.warn(`Unknown analytics provider: ${provider}`);
  }
}

/**
 * Sanitize event data to remove PII
 */
function sanitizeEventData(data?: AnalyticsData): AnalyticsData | undefined {
  if (!data || typeof data !== 'object') return undefined;

  const sanitized: Record<string, AnalyticsDataValue> = {};
  const piiKeys = [
    "email",
    "name",
    "firstName",
    "lastName",
    "phone",
    "address",
    "city",
    "state",
    "zip",
    "postalCode",
    "fullName",
    "userEmail",
    "customerEmail",
    "vendorEmail",
  ];

  for (const [key, value] of Object.entries(data)) {
    // Skip PII keys
    if (piiKeys.some((piiKey) => key.toLowerCase().includes(piiKey.toLowerCase()))) {
      continue;
    }

    // Only include safe types
    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean" ||
      value === null ||
      value === undefined
    ) {
      sanitized[key] = value;
    } else if (typeof value === "object" && !Array.isArray(value) && value !== null) {
      // Recursively sanitize nested objects
      const nested = sanitizeEventData(value as Record<string, AnalyticsDataValue>);
      if (nested && typeof nested === 'object' && Object.keys(nested).length > 0) {
        sanitized[key] = nested;
      }
    }
  }

  return sanitized;
}

// Convenience functions for common events
export const analytics = {
  pageView: (path: string, data?: AnalyticsData) => {
    trackEvent(AnalyticsEvent.PAGE_VIEW, {
      path,
      ...data,
    });
  },

  listingView: (listingId: string, data?: AnalyticsData) => {
    trackEvent(AnalyticsEvent.LISTING_VIEW, {
      listing_id: listingId,
      ...data,
    });
  },

  availabilityOpened: (listingId: string, data?: AnalyticsData) => {
    trackEvent(AnalyticsEvent.AVAILABILITY_OPENED, {
      listing_id: listingId,
      ...data,
    });
  },

  dateSelected: (listingId: string, date: string, data?: AnalyticsData) => {
    trackEvent(AnalyticsEvent.DATE_SELECTED, {
      listing_id: listingId,
      date,
      ...data,
    });
  },

  startCheckout: (listingId: string, data?: AnalyticsData) => {
    trackEvent(AnalyticsEvent.START_CHECKOUT, {
      listing_id: listingId,
      ...data,
    });
  },

  openContactVendor: (listingId: string, vendorId: string, data?: AnalyticsData) => {
    trackEvent(AnalyticsEvent.OPEN_CONTACT_VENDOR, {
      listing_id: listingId,
      vendor_id: vendorId,
      ...data,
    });
  },

  openVendorProfile: (vendorId: string, data?: AnalyticsData) => {
    trackEvent(AnalyticsEvent.OPEN_VENDOR_PROFILE, {
      vendor_id: vendorId,
      ...data,
    });
  },

  sendMessageClient: (conversationId: string, data?: AnalyticsData) => {
    trackEvent(AnalyticsEvent.SEND_MESSAGE_CLIENT, {
      conversation_id: conversationId,
      ...data,
    });
  },
};

